import React from 'react';
import { Workspace } from '../../../types/workspace';

interface WorkspaceFilterProps {
  workspaces: Workspace[];
  selectedWorkspaceId: number | null | 'global'; // null = "Todos", 'global' = "Global (TD)"
  onWorkspaceChange: (workspaceId: number | null | 'global') => void;
  isLoading?: boolean; // Loading state
  className?: string;
}

/**
 * WorkspaceFilter Component
 *
 * Filtro reutilizável de workspace para telas de listagem (Agentes, Bases, Data Sources).
 * Permite filtrar por workspace específico ou visualizar todos os workspaces.
 *
 * Props:
 * - workspaces: Lista de workspaces disponíveis
 * - selectedWorkspaceId: ID do workspace selecionado (null = todos)
 * - onWorkspaceChange: Callback quando workspace muda
 * - isLoading: Estado de carregamento
 * - className: Classes CSS adicionais
 */
export const WorkspaceFilter: React.FC<WorkspaceFilterProps> = ({
  workspaces,
  selectedWorkspaceId,
  onWorkspaceChange,
  isLoading = false,
  className = '',
}) => {
  return (
    <div className={`flex items-center gap-3 ${className}`}>
      <div className="relative">
        <select
          value={
            selectedWorkspaceId === null
              ? 'all'
              : selectedWorkspaceId === 'global'
              ? 'global'
              : selectedWorkspaceId.toString()
          }
          onChange={(e) => {
            const value = e.target.value;
            if (value === 'all') {
              onWorkspaceChange(null);
            } else if (value === 'global') {
              onWorkspaceChange('global');
            } else {
              onWorkspaceChange(parseInt(value, 10));
            }
          }}
          disabled={isLoading}
          className={`
            pl-4 pr-8 py-2.5
            bg-[#1a1a1a] border border-[#333333]
            text-white text-sm
            rounded-lg
            focus:outline-none focus:border-[#F4CC04]
            hover:border-[#444444]
            transition-colors
            min-w-[200px]
            ${isLoading ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
          `}
        >
        <option value="all" className="bg-[#1a1a1a] text-white">
          Todos os Workspaces
        </option>

        <option value="global" className="bg-[#1a1a1a] text-white">
          Global (TD)
        </option>

        {workspaces.length > 0 && workspaces.map((workspace) => (
          <option
            key={workspace.id}
            value={workspace.id}
            className="bg-[#1a1a1a] text-white"
          >
            {workspace.name}
          </option>
        ))}
      </select>

      </div>

      {/* Workspace info badge */}
      {!isLoading && selectedWorkspaceId && (
        <div className="flex items-center gap-2 px-3 py-1.5 bg-[#F4CC04]/10 border border-[#F4CC04]/30 rounded-lg">
          <div className="w-2 h-2 rounded-full bg-[#F4CC04] animate-pulse" />
          <span className="text-xs text-[#F4CC04] font-medium">
            Filtrando: {
              selectedWorkspaceId === 'global'
                ? 'Global (TD)'
                : workspaces.find(w => w.id === selectedWorkspaceId)?.name
            }
          </span>
        </div>
      )}
    </div>
  );
};

export default WorkspaceFilter;
